/**
 * Diplomacy System - manages diplomatic relations, treaties, and actions between factions
 */

import type { World, System } from '@/core/ecs/World';
import type { FactionManager } from '@/game/factions/FactionManager';
import { EventBus } from '@/core/events/EventBus';
import type { FactionId } from '@/types';

export type RelationLevel = 'war' | 'hostile' | 'neutral' | 'friendly' | 'alliance';

export type TreatyType = 'non_aggression' | 'trade' | 'alliance';

export interface Treaty {
  id: number;
  type: TreatyType;
  parties: [FactionId, FactionId];
  startTime: number;
  duration: number; // In game seconds, 0 for permanent until broken
  effects: TreatyEffects;
}

export interface TreatyEffects {
  relationBonus?: number;
  incomeBonus?: number;
  sharedVision?: boolean;
  cannotDeclareWar?: boolean;
}

export const TREATY_TEMPLATES: Record<TreatyType, Omit<Treaty, 'id' | 'parties' | 'startTime'>> = {
  non_aggression: {
    type: 'non_aggression',
    duration: 180, // 3 minutes
    effects: {
      relationBonus: 20,
      cannotDeclareWar: true,
    },
  },
  trade: {
    type: 'trade',
    duration: 0, // Permanent until broken
    effects: {
      relationBonus: 30,
      incomeBonus: 0.1, // 10% income bonus
    },
  },
  alliance: {
    type: 'alliance',
    duration: 0, // Permanent until broken
    effects: {
      relationBonus: 60,
      sharedVision: true,
      cannotDeclareWar: true,
    },
  },
};

export class DiplomacySystem implements System {
  private factionManager: FactionManager;
  private treaties: Map<number, Treaty> = new Map();
  private nextTreatyId: number = 1;
  private gameTime: number = 0;

  // Pending diplomatic proposals
  private proposals: Map<string, { from: FactionId; to: FactionId; type: TreatyType; time: number }> = new Map();

  constructor(factionManager: FactionManager) {
    this.factionManager = factionManager;

    // Set up treaty checker so FactionManager.isEnemy considers treaties
    this.factionManager.setTreatyChecker((a, b) => {
      return this.hasActiveTreaty(a, b, 'non_aggression') ||
             this.hasActiveTreaty(a, b, 'alliance');
    });
  }

  update(_world: World, deltaTime: number): void {
    this.gameTime += deltaTime;

    // Check for expired treaties
    for (const [id, treaty] of this.treaties) {
      if (treaty.duration > 0 && this.gameTime - treaty.startTime >= treaty.duration) {
        this.expireTreaty(id);
      }
    }

    // Clean up old proposals (expire after 30 seconds)
    for (const [key, proposal] of this.proposals) {
      if (this.gameTime - proposal.time > 30) {
        this.proposals.delete(key);
      }
    }
  }

  /**
   * Gets the diplomatic relation level between two factions
   */
  getRelationLevel(a: FactionId, b: FactionId): RelationLevel {
    const value = this.factionManager.getRelation(a, b);

    // Treaties override base relation level
    if (this.hasActiveTreaty(a, b, 'alliance')) {
      return 'alliance';
    }
    if (this.hasActiveTreaty(a, b, 'non_aggression')) {
      // Non-aggression pact means at least neutral
      if (value <= -60) return 'neutral';
      if (value <= -20) return 'neutral';
    }

    if (value <= -60) return 'war';
    if (value <= -20) return 'hostile';
    if (value <= 19) return 'neutral';
    if (value <= 59) return 'friendly';
    return 'alliance';
  }

  /**
   * Declare war on another faction
   */
  declareWar(attacker: FactionId, target: FactionId): boolean {
    // Check if there's a non-aggression pact
    if (this.hasActiveTreaty(attacker, target, 'non_aggression') ||
        this.hasActiveTreaty(attacker, target, 'alliance')) {
      return false;
    }

    // Break any existing treaties
    this.breakAllTreaties(attacker, target);

    // Set hostile relations
    this.factionManager.setRelation(attacker, target, -80);

    EventBus.emit('diplomacy:war', { attacker, target });

    return true;
  }

  /**
   * Propose peace to end a war
   */
  proposePeace(from: FactionId, to: FactionId): void {
    const key = this.getProposalKey(from, to, 'peace');
    this.proposals.set(key, { from, to, type: 'non_aggression', time: this.gameTime });

    EventBus.emit('diplomacy:proposal', { from, to, type: 'peace' });
  }

  /**
   * Accept a peace proposal
   */
  acceptPeace(from: FactionId, to: FactionId): boolean {
    const key = this.getProposalKey(to, from, 'peace'); // Note: reversed
    if (!this.proposals.has(key)) return false;

    this.proposals.delete(key);

    // Set neutral relations
    this.factionManager.setRelation(from, to, 0);

    EventBus.emit('diplomacy:peace', { from: to, to: from });

    return true;
  }

  /**
   * Propose a treaty
   */
  proposeTreaty(from: FactionId, to: FactionId, type: TreatyType): void {
    const key = this.getProposalKey(from, to, type);
    this.proposals.set(key, { from, to, type, time: this.gameTime });

    EventBus.emit('diplomacy:proposal', { from, to, type });
  }

  /**
   * Accept a treaty proposal
   */
  acceptTreaty(from: FactionId, to: FactionId, type: TreatyType): boolean {
    const key = this.getProposalKey(to, from, type); // Note: reversed
    if (!this.proposals.has(key)) return false;

    this.proposals.delete(key);
    return this.createTreaty(from, to, type);
  }

  /**
   * Reject a proposal
   */
  rejectProposal(from: FactionId, to: FactionId, type: TreatyType | 'peace'): void {
    const key = this.getProposalKey(to, from, type);
    this.proposals.delete(key);

    EventBus.emit('diplomacy:rejected', { from: to, to: from, type });
  }

  /**
   * Create a new treaty between two factions
   */
  createTreaty(a: FactionId, b: FactionId, type: TreatyType): boolean {
    // Check if they're at war
    if (this.getRelationLevel(a, b) === 'war') {
      return false;
    }

    // Check if treaty already exists
    if (this.hasActiveTreaty(a, b, type)) {
      return false;
    }

    const template = TREATY_TEMPLATES[type];
    const treaty: Treaty = {
      id: this.nextTreatyId++,
      type,
      parties: [a, b],
      startTime: this.gameTime,
      duration: template.duration,
      effects: { ...template.effects },
    };

    this.treaties.set(treaty.id, treaty);

    // Apply relation bonus
    if (treaty.effects.relationBonus) {
      const currentRelation = this.factionManager.getRelation(a, b);
      this.factionManager.setRelation(a, b, currentRelation + treaty.effects.relationBonus);
    }

    EventBus.emit('diplomacy:treaty', { type, parties: [a, b] });

    return true;
  }

  /**
   * Break a treaty
   */
  breakTreaty(treatyId: number, breaker: FactionId): boolean {
    const treaty = this.treaties.get(treatyId);
    if (!treaty) return false;

    // Determine the other party
    const other = treaty.parties[0] === breaker ? treaty.parties[1] : treaty.parties[0];

    // Relation penalty for breaking treaty
    const currentRelation = this.factionManager.getRelation(breaker, other);
    this.factionManager.setRelation(breaker, other, currentRelation - 40);

    this.treaties.delete(treatyId);

    EventBus.emit('diplomacy:broken', { type: treaty.type, breaker, other });

    return true;
  }

  /**
   * Break all treaties between two factions
   */
  private breakAllTreaties(a: FactionId, b: FactionId): void {
    for (const [id, treaty] of this.treaties) {
      if ((treaty.parties[0] === a && treaty.parties[1] === b) ||
          (treaty.parties[0] === b && treaty.parties[1] === a)) {
        this.treaties.delete(id);
      }
    }
  }

  /**
   * Expire a treaty naturally
   */
  private expireTreaty(treatyId: number): void {
    const treaty = this.treaties.get(treatyId);
    if (!treaty) return;

    this.treaties.delete(treatyId);

    EventBus.emit('diplomacy:expired', { type: treaty.type, parties: treaty.parties });
  }

  /**
   * Check if a treaty type exists between two factions
   */
  hasActiveTreaty(a: FactionId, b: FactionId, type: TreatyType): boolean {
    for (const treaty of this.treaties.values()) {
      if (treaty.type === type) {
        if ((treaty.parties[0] === a && treaty.parties[1] === b) ||
            (treaty.parties[0] === b && treaty.parties[1] === a)) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Get all treaties for a faction
   */
  getTreatiesFor(factionId: FactionId): Treaty[] {
    const result: Treaty[] = [];
    for (const treaty of this.treaties.values()) {
      if (treaty.parties.includes(factionId)) {
        result.push(treaty);
      }
    }
    return result;
  }

  /**
   * Get income bonus from trade treaties
   */
  getIncomeBonus(factionId: FactionId): number {
    let bonus = 0;
    for (const treaty of this.treaties.values()) {
      if (treaty.parties.includes(factionId) && treaty.effects.incomeBonus) {
        bonus += treaty.effects.incomeBonus;
      }
    }
    return bonus;
  }

  /**
   * Check if factions share vision (allied)
   */
  hasSharedVision(a: FactionId, b: FactionId): boolean {
    for (const treaty of this.treaties.values()) {
      if (treaty.effects.sharedVision) {
        if ((treaty.parties[0] === a && treaty.parties[1] === b) ||
            (treaty.parties[0] === b && treaty.parties[1] === a)) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Get pending proposals for a faction
   */
  getPendingProposals(factionId: FactionId): Array<{ from: FactionId; type: TreatyType | 'peace' }> {
    const result: Array<{ from: FactionId; type: TreatyType | 'peace' }> = [];
    for (const [key, proposal] of this.proposals) {
      if (proposal.to === factionId) {
        result.push({ from: proposal.from, type: proposal.type });
      }
    }
    return result;
  }

  private getProposalKey(from: FactionId, to: FactionId, type: string): string {
    return `${from}:${to}:${type}`;
  }

  /**
   * Modify relation between factions (for events like border skirmishes)
   */
  modifyRelation(a: FactionId, b: FactionId, delta: number): void {
    const current = this.factionManager.getRelation(a, b);
    this.factionManager.setRelation(a, b, current + delta);
  }
}
